/**
 * ToolCallsSummary · AI 思考过程展示（v5）
 *
 * v5 改造：单一折叠头部 · 默认收起 · 点击下拉展开时间轴
 *  · streaming 中  → 头部显示"当前在做什么"（overrideText / getStreamingLabel /
 *                   按 elapsed 时间分段兜底）· 默认收起
 *  · done         → 头部显示「已思考 X 秒」 · 默认收起
 *  · 点 head 展开 → 显示完整时间轴（保留之前样式：● 工具名 · 入参 ─ 结果 + 耗时）
 *
 * v4.6 老版本是"streaming 时父级 MessageAssistant 顶部一个浅蓝气泡 + 下方常驻列表"
 * 双重展示，太重。新版收成单条，更接近 ChatGPT/Claude 的折叠式 thinking。
 *
 * 思考时长 freeze（修 bad case：done 后秒数还在涨）
 *  · 旧实现 totalSec = round(Date.now() - messageStartedAt)，每次 re-render 都重算
 *  · 任意外部触发 re-render（用户在 Composer 输入 / 别的卡片 hover）秒数继续涨
 *  · 新实现用 doneAtRef 在 streaming → done 第一次切换时捕获 Date.now()，之后所有
 *    render 都用 doneAtRef.current 当 endTime · 秒数被冻结
 *
 * 状态流转：
 *   tool_start → record { id, toolName, paramsLabel } 占位（无 durationMs）
 *   tool_end   → 同 id update 完成态（resultSummary + durationMs + ok）
 *
 * 显示翻译：toolDisplayMap.TOOL_DISPLAY_NAME 把 toolName → 业务语言；
 * 后端将来在事件里给 display_name 字段时，前端优先用该字段（暂未到位，留口子）。
 */

import {
  CheckCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  RightOutlined,
  ThunderboltOutlined
} from '@ant-design/icons'
import React, { useEffect, useRef, useState } from 'react'
import type { ToolCallRecord } from '../../../../../../../types/ai-classify'
import { displayToolName } from '../../../../../services/toolDisplayMap'
import styles from './card.module.scss'

interface Props {
  calls: ToolCallRecord[]
  /** streaming 中 · head 显示当前进度；done 显示「已思考 X 秒」 */
  streaming?: boolean
  /**
   * 消息起始时间戳（ms）· 用于"已思考 X 秒"按 wall-clock 计算总耗时。
   *
   * bad case #16：原 totalMs 累加各 tool durationMs，但工具实际秒级很快（0.1s）
   * 不含 LLM 流式输出 + 网络排队时间，用户看到"已思考 1 秒"但实际等了 8 秒。
   * 改为优先用 wall-clock (now - messageStartedAt) 表达真实总等待时长。
   */
  messageStartedAt?: number
  /**
   * streaming 期间的"当前在做什么"override 文本 · 比如"正在创建相册…"之类的进度文案，
   * 由外部业务逻辑按需注入，比 getStreamingLabel 更具体
   *  · 没传时按 getStreamingLabel(calls) 推断
   *  · calls 也空 + 没传 override → 按 elapsed 时间分段兜底
   */
  overrideStreamingText?: string
}

/** 单条记录是否已完成（end 事件给了 resultSummary 或 durationMs） */
function isCallDone(c: ToolCallRecord): boolean {
  return typeof c.durationMs === 'number' || typeof c.resultSummary === 'string'
}

/**
 * 时间轴节点（2026-05-15 重构 · 参考截图 27 用户视角）
 *
 * 渲染时把 ToolCallRecord 序列展开成时间轴 TimelineNode 列表, 节点分两种:
 *   · tool       工具调用节点  (复用现有 ● 状态图标 + 工具中文名 + 入参 + 结果)
 *   · thinking   思考间隙节点  (灰圆点 + "正在 X" 文案, 表示 AI 在思考/汇总)
 *
 * 测量原则:
 *   · thinking 时长 = 上一个 tool.endedAtMs (或 messageStartedAt) → 下一个 tool.startedAtMs
 *     的 wall-clock 间隔。最后一段 (最后 tool → done) 用 doneAt - 最后 tool.endedAtMs
 *   · 间隔 < 600ms 不插入思考节点 (避免噪声)
 *   · 没有 startedAtMs/endedAtMs 时降级为只渲染工具节点 (老消息兼容)
 */
interface TimelineNode {
  kind: 'tool' | 'thinking'
  /** 节点显示文案 (业务语言, 不出现工具名/英文) */
  label: string
  /** 节点耗时毫秒 */
  durationMs: number
  /** 仅 tool 节点: 结果摘要 */
  resultSummary?: string
  /** 仅 tool 节点: 是否完成 */
  done?: boolean
  /** 仅 tool 节点: 是否失败 */
  failed?: boolean
  /** 仅 tool 节点: 入参中文摘要 */
  paramsLabel?: string
  /** stable key */
  key: string
}

/** 阶段思考文案 · 按位置语义化, 让用户感知 AI 在干什么 (参考截图 27)。 */
function thinkingLabelFor(
  position: 'before-first' | 'between' | 'after-last'
): string {
  switch (position) {
    case 'before-first':
      return '正在分析你的需求'
    case 'between':
      return '正在评估下一步'
    case 'after-last':
      return '正在汇总数据'
  }
}

/** 间隔 < 此值不插思考节点, 避免噪声 (网络往返/事件抖动可造成短间隔) */
const MIN_THINKING_GAP_MS = 600

/**
 * 把 calls 展开成时间轴节点序列。
 * messageStartedAt / streaming / doneAt 用于计算首尾思考期。
 */
function buildTimeline(
  calls: ToolCallRecord[],
  messageStartedAt: number | undefined,
  streaming: boolean,
  doneAt: number | null
): TimelineNode[] {
  if (calls.length === 0) return []
  // 按 startedAtMs 排序; 缺失字段的排到末尾
  const sorted = [...calls].sort((a, b) => {
    const sa = a.startedAtMs ?? Number.MAX_SAFE_INTEGER
    const sb = b.startedAtMs ?? Number.MAX_SAFE_INTEGER
    return sa - sb
  })
  // 老消息没 startedAtMs 时降级: 仅渲染工具节点
  const hasTs = sorted.every((c) => typeof c.startedAtMs === 'number')
  if (!hasTs) {
    return sorted.map((c, i) => ({
      kind: 'tool',
      label: displayToolName(c.toolName),
      durationMs: c.durationMs ?? 0,
      resultSummary: c.resultSummary,
      done: isCallDone(c),
      failed: c.ok === false,
      paramsLabel: c.paramsLabel,
      key: c.id ?? `t${i}`
    }))
  }

  const nodes: TimelineNode[] = []
  // 1) 首段思考: messageStartedAt → 第一个 tool.startedAtMs
  if (
    typeof messageStartedAt === 'number' &&
    typeof sorted[0].startedAtMs === 'number'
  ) {
    const gap = sorted[0].startedAtMs! - messageStartedAt
    if (gap >= MIN_THINKING_GAP_MS) {
      nodes.push({
        kind: 'thinking',
        label: thinkingLabelFor('before-first'),
        durationMs: gap,
        key: 'think-pre'
      })
    }
  }
  // 2) 工具 + 工具间隙
  for (let i = 0; i < sorted.length; i++) {
    const c = sorted[i]
    nodes.push({
      kind: 'tool',
      label: displayToolName(c.toolName),
      durationMs: c.durationMs ?? 0,
      resultSummary: c.resultSummary,
      done: isCallDone(c),
      failed: c.ok === false,
      paramsLabel: c.paramsLabel,
      key: c.id ?? `t${i}`
    })
    // 中间思考期: 当前 endedAtMs → 下一个 startedAtMs
    if (i < sorted.length - 1) {
      const cur = sorted[i]
      const next = sorted[i + 1]
      if (
        typeof cur.endedAtMs === 'number' &&
        typeof next.startedAtMs === 'number'
      ) {
        const gap = next.startedAtMs - cur.endedAtMs
        if (gap >= MIN_THINKING_GAP_MS) {
          nodes.push({
            kind: 'thinking',
            label: thinkingLabelFor('between'),
            durationMs: gap,
            key: `think-mid-${i}`
          })
        }
      }
    }
  }
  // 3) 尾段思考: 最后 tool.endedAtMs → doneAt (仅 streaming=false 时才有终点)
  if (!streaming && doneAt !== null) {
    const last = sorted[sorted.length - 1]
    if (typeof last.endedAtMs === 'number') {
      const gap = doneAt - last.endedAtMs
      if (gap >= MIN_THINKING_GAP_MS) {
        nodes.push({
          kind: 'thinking',
          label: thinkingLabelFor('after-last'),
          durationMs: gap,
          key: 'think-post'
        })
      }
    }
  }
  return nodes
}

/**
 * 计算 streaming 期间"当前 AI 在做什么"的语言化描述
 *
 *  · 有未完成工具 → "AI 思考中：当前工具「入参」"
 *  · 全部完成但 LLM 没出 final → 按已完成工具组合推断业务阶段
 *  · 还没工具 → "AI 思考中..."
 */
export function getStreamingLabel(calls: ToolCallRecord[] | undefined): string {
  if (!calls || calls.length === 0) return 'AI 思考中...'
  const inflight = calls.find((c) => !isCallDone(c))
  if (inflight) {
    const action = displayToolName(inflight.toolName)
    return inflight.paramsLabel
      ? `AI 思考中：${action}「${inflight.paramsLabel}」`
      : `AI 思考中：${action}…`
  }
  return inferBusyStage(calls)
}

/**
 * 工具调用全部完成、LLM 还在汇总输出阶段时的语言化进度。
 *
 * 不再统一显示"整理结果…（已完成 N 个工具）" — 用户看到一堆工程化工具名意义有限。
 * 改成按"最近一组同类工具"反推业务阶段，更贴近用户做的事：
 *   · 大量 user/department 类工具 → "正在整理团队成员名单…"
 *   · search_photos / get_watermark_content → "正在汇总照片样本…"
 *   · ranking → "正在统计成员拍摄量…"
 *   · 其他混合 → 兜底"正在汇总分析结果…"
 */
function inferBusyStage(calls: ToolCallRecord[]): string {
  const names = calls.map((c) => c.toolName ?? '')
  const has = (kw: string): boolean => names.some((n) => n.includes(kw))
  if (has('ranking')) return '正在统计成员拍摄量…'
  if (has('department') || has('group_users') || has('users_by')) {
    return '正在整理团队成员名单…'
  }
  if (has('search_photos') || has('ai_search')) {
    return '正在汇总照片样本…'
  }
  if (has('watermark')) return '正在分析水印字段命中…'
  return '正在汇总分析结果…'
}

/**
 * streaming 时还没拿到工具调用 + 也没 override 文本的兜底文案
 * 按 elapsed 时间分段，让用户即使在 §5.4 命中预注入直接出方案的场景也能看到
 * "在工作中" 的反馈，不至于死等空白。
 */
function elapsedFallbackLabel(elapsedMs: number): string {
  const elapsed = elapsedMs / 1000
  if (elapsed >= 12) return '正在汇总分类方案…'
  if (elapsed >= 6) return '正在评估分类维度…'
  if (elapsed >= 2) return '正在分析水印数据…'
  return 'AI 思考中…'
}

const ToolCallsSummary: React.FC<Props> = ({
  calls,
  streaming = false,
  messageStartedAt,
  overrideStreamingText
}) => {
  // 默认折叠 · 用户手动展开后保持
  const [open, setOpen] = useState(false)

  /**
   * doneAtRef · 在 streaming → done 第一次切换时捕获 Date.now()
   *  · 修 bad case：done 后任意 re-render 还在用 Date.now() 让秒数继续涨
   *  · 用 ref 而非 state · 不需要触发额外 re-render
   *  · 一旦设上就不再变 · 之后 totalSec 永远算同一个差值
   */
  const doneAtRef = useRef<number | null>(null)

  /**
   * sawStreamingRef · 本组件实例是否曾观察到 streaming === true
   *  · 修 bad case 2：历史会话加载的消息 streaming 从一开始就是 false ·
   *    useEffect 会立即把 doneAtRef 设成"打开抽屉这一刻的 Date.now()" ·
   *    再用 wall-clock 算 totalSec = (Date.now() - messageStartedAt) 直接是
   *    "从消息创建至今"的差值（可能几小时 / 几天）· 每次重开抽屉都越涨越大
   *  · 修复：仅当本实例真的见过 streaming=true 才信任 wall-clock
   *    历史消息（一直是 done）退化用 fallbackMs（各工具 duration 之和）
   *  · useRef(streaming) 初始值 = mount 时 streaming 值 · live 消息 mount 时
   *    通常已是 streaming=true → 自动开启 wall-clock 路径
   */
  const sawStreamingRef = useRef<boolean>(streaming)
  useEffect(() => {
    if (streaming) sawStreamingRef.current = true
    if (!streaming && sawStreamingRef.current && doneAtRef.current === null) {
      doneAtRef.current = Date.now()
    }
  }, [streaming])

  /**
   * 仅当 streaming 期间没有工具 + 没有 override 文本时才需要每秒 tick
   * 让 elapsedFallbackLabel 能从"AI 思考中…"切到"正在分析水印数据…"等
   * 其他情况 head 文案不依赖时间，不开 timer 浪费帧
   */
  const needTick =
    streaming && (!calls || calls.length === 0) && !overrideStreamingText
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    if (!needTick) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [needTick])

  // ─── 思考时长 · 算 totalSec ───
  // fallbackMs：各工具 duration 之和（历史消息没法算 wall-clock 时退化用这个）
  const fallbackMs = calls.reduce(
    (sum, c) => sum + (typeof c.durationMs === 'number' ? c.durationMs : 0),
    0
  )
  let effectiveMs = fallbackMs
  // 只有本实例真的见过 streaming=true 才信任 wall-clock
  // 历史消息（一直 done）跳过 → 用 fallbackMs，避免把"消息创建到当前打开"误算成思考时长
  const useWallClock =
    sawStreamingRef.current &&
    typeof messageStartedAt === 'number' &&
    messageStartedAt > 0
  if (useWallClock) {
    // streaming 时用 Date.now() 实时（不 tick 也无所谓，head 不展示秒数）
    // done 时用 doneAtRef.current 冻结
    const endTime = streaming ? Date.now() : (doneAtRef.current ?? Date.now())
    const wall = Math.max(0, endTime - messageStartedAt!)
    if (wall > effectiveMs) effectiveMs = wall
  }
  /**
   * 判定本条消息是否有"靠谱时长"
   *  · 本实例见过 streaming → wall-clock 是真值
   *  · 否则要 fallbackMs > 0（后端给了 tool 的 duration）
   *  · 两者皆无 → 不显示秒数，避免"1 秒"误导（fallbackMs=0 算出来永远 1 秒）
   */
  const hasReliableDuration = useWallClock || fallbackMs > 0
  const totalSec = Math.max(1, Math.round(effectiveMs / 1000))

  // ─── 头部文案 ───
  const headLabel = (() => {
    if (!streaming) {
      return hasReliableDuration ? `已思考 ${totalSec} 秒` : '已完成思考'
    }
    if (overrideStreamingText) return overrideStreamingText
    if (calls && calls.length > 0) return getStreamingLabel(calls)
    if (typeof messageStartedAt === 'number' && messageStartedAt > 0) {
      return elapsedFallbackLabel(now - messageStartedAt)
    }
    return 'AI 思考中…'
  })()

  // ─── chevron 展开按钮显隐 · calls 空时无内容可展开，head 单独显示 ───
  const showChevron = calls.length > 0

  // streaming + 无 override + 无 calls → 仍渲染 head 表达"AI 在工作"
  // done + 无 calls → 没东西展示，return null（finalText 由父组件渲染）
  if (!streaming && calls.length === 0) return null

  return (
    <div className={styles.toolWrap}>
      <button
        type="button"
        className={styles.toolHead}
        onClick={() => {
          if (!showChevron) return
          setOpen((v) => !v)
        }}
        aria-expanded={open}
      >
        {/* 左侧 icon · streaming 用 loading 旋转，done 用 thunderbolt（与 AI 头像呼应） */}
        {streaming ? (
          <LoadingOutlined
            style={{
              fontSize: 12,
              marginRight: 6,
              color: streaming ? '#6366f1' : '#a78bfa'
            }}
          />
        ) : (
          <ThunderboltOutlined
            style={{
              fontSize: 12,
              marginRight: 6,
              color: streaming ? '#6366f1' : '#a78bfa'
            }}
          />
        )}
        <span className={styles.toolHeadLabel}>{headLabel}</span>
        {/* 右侧 chevron · 仅 calls 非空时可点开收 */}
        {showChevron &&
          (open ? (
            <DownOutlined
              style={{
                fontSize: 10,
                marginLeft: 6,
                color: '#86909c'
              }}
            />
          ) : (
            <RightOutlined
              style={{
                fontSize: 10,
                marginLeft: 6,
                color: '#86909c'
              }}
            />
          ))}
      </button>
      {open && showChevron && (
        <ul className={styles.toolList}>
          {buildTimeline(
            calls,
            messageStartedAt,
            streaming,
            doneAtRef.current
          ).map((n) => {
            if (n.kind === 'thinking') {
              return (
                <li key={n.key} className={styles.toolItem}>
                  <span className={styles.toolTimelineCol}>
                    {/*
                     * 思考期节点 · 灰色 ✓
                     * buildTimeline 只在两个端点都已知时合成 thinking 节点
                     * (首段=第一个 tool 已 start; 中间=下一个 tool 已 start;
                     *  尾段=已到 done), 所以一出现就是"已完成"。
                     * 用灰色 ✓ 表达完成态 · 与工具的绿色 ✓ 在颜色上区分,
                     * 避免之前 clock-circle 被误读为 loading 未完成。
                     */}
                    <CheckCircleOutlined
                      className={styles.toolNodeIcon}
                      style={{ fontSize: 12, color: '#9ca3af' }}
                    />
                  </span>
                  <span className={styles.toolItemLeft}>
                    <span
                      className={styles.toolName}
                      style={{ color: '#6b7280' }}
                    >
                      {n.label}
                    </span>
                  </span>
                  <span className={styles.toolItemRight}>
                    <span className={styles.toolMs}>
                      {(n.durationMs / 1000).toFixed(1)}s
                    </span>
                  </span>
                </li>
              )
            }
            // tool 节点
            return (
              <li
                key={n.key}
                className={`${styles.toolItem} ${
                  !n.done ? styles.toolItemRunning : ''
                } ${n.failed ? styles.toolItemFailed : ''}`}
              >
                <span className={styles.toolTimelineCol}>
                  {n.done ? (
                    n.failed ? (
                      <ExclamationCircleOutlined
                        className={styles.toolNodeIcon}
                        style={{ fontSize: 12, color: '#f5222d' }}
                      />
                    ) : (
                      <CheckCircleOutlined
                        className={styles.toolNodeIcon}
                        style={{ fontSize: 12, color: '#52c41a' }}
                      />
                    )
                  ) : (
                    <LoadingOutlined
                      className={styles.toolNodeIcon}
                      style={{ fontSize: 12, color: '#6366f1' }}
                    />
                  )}
                </span>
                <span className={styles.toolItemLeft}>
                  <span className={styles.toolName}>{n.label}</span>
                  {n.paramsLabel && (
                    <span className={styles.toolParam}>· {n.paramsLabel}</span>
                  )}
                </span>
                <span className={styles.toolItemRight}>
                  {n.done ? (
                    <>
                      {n.resultSummary && (
                        <span className={styles.toolResult}>
                          {n.resultSummary}
                        </span>
                      )}
                      {n.durationMs > 0 && (
                        <span className={styles.toolMs}>
                          {(n.durationMs / 1000).toFixed(1)}s
                        </span>
                      )}
                    </>
                  ) : (
                    <span className={styles.toolResult}>执行中…</span>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default ToolCallsSummary
